import { useCallback } from 'react'
import type { BetSource, RaceDetails } from '@mobi/betslip/types'
import type { MeetingInformationDataTransferObject } from '@mobi/api-types'
import {
  trackRacePreviewBetClicked,
  trackTipsBetClicked,
} from '@core/Areas/Racing/Components/BettableTips/analytics'
import { makeSelection } from '@core/Areas/Racing/Components/BettableTips/helpers'
import { PriceSources } from '@core/Data/Betting/selections'
import type { Acceptor } from '@core/Areas/Racing/Hooks/useAcceptors'
import { handleBetSelection } from '@core/Utils/betting/handleBetSelection'

type Props = {
  meeting: MeetingInformationDataTransferObject
  acceptor: Acceptor
  isFixedOdds: boolean
  priceSource: PriceSources
  buttonPosition: number
  tipSource: BetSource
}

export function useBettableTipQuickBetting({
  meeting,
  acceptor,
  isFixedOdds,
  priceSource,
  buttonPosition,
  tipSource,
}: Props) {
  const loadQuickBet = useCallback(() => {
    const selectedRace = meeting.SelectedRace

    const [bettingType, selection] = makeSelection(meeting, acceptor, isFixedOdds, priceSource)

    const shouldAllowPlaceInvestment = isFixedOdds
      ? (meeting.SelectedRace.FixedOddsInfo?.IsPlaceAvailable ?? false)
      : meeting.SelectedRace.HasPlacePool

    handleBetSelection({
      betFlow: { location: 'Quickbet' },
      selections: [
        {
          shouldAllowPlaceInvestment,
          isEachWayAvailable: false,
          bettingType,
          selection,
          selectionDetails: {
            acceptors: [acceptor],
            races: [
              {
                meetingDate: meeting.MeetingDate,
                raceTime: selectedRace.RaceTime,
                acceptorKeys: [acceptor.key],
                raceNumber: selectedRace.RaceNumber,
                key: selectedRace.Key,
                type: selectedRace.RaceType,
                distance: selectedRace.Distance,
                isFixedOddsRace: selectedRace.IsFixedOddsRace,
                meetingCode: meeting.MeetingCode,
                meetingId: meeting.MeetingId,
                meetingName: meeting.MeetingName,
              },
            ],
          } as RaceDetails,
          betSource: tipSource,
        },
      ],
    })

    const trackFn = tipSource === 'tips' ? trackTipsBetClicked : trackRacePreviewBetClicked

    trackFn(meeting.MeetingCode, meeting.MeetingName, buttonPosition, 'Win & Place')
  }, [meeting, acceptor, isFixedOdds, priceSource, buttonPosition, tipSource])

  return {
    loadQuickBet,
  }
}
